// @spec GHG-STR-050
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmissionTargets } from '@/features/ghg/api/use-emission-targets';
import { useReductionInitiatives } from '@/features/ghg/api/use-reduction-initiatives';
import {
  useDeleteEmissionScenario,
  useDuplicateEmissionScenario,
  useEmissionScenario,
  useUpdateEmissionScenario,
} from '@/features/ghg/api/use-scenarios';
import { ScenarioDetail } from '@/features/ghg/components/ScenarioDetail';
import { ScenarioForm } from '@/features/ghg/components/ScenarioForm';
import type {
  CustomAdjustmentInput,
  EmissionScenarioFormValues,
  ScenarioStatus,
} from '@/features/ghg/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/scenarios/$scenarioId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.scenarioDetails'),
  }),
  component: ScenarioDetailPage,
});

/**
 * Scenario detail page.
 *
 * Displays comprehensive scenario details including:
 * - Summary metrics
 * - Projection chart
 * - Initiative breakdown
 * - Edit, duplicate, and delete actions
 */
function ScenarioDetailPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { scenarioId } = Route.useParams();

  const { scenario, isLoading, isError } = useEmissionScenario(scenarioId);
  const updateScenario = useUpdateEmissionScenario();
  const deleteScenario = useDeleteEmissionScenario();
  const duplicateScenario = useDuplicateEmissionScenario();
  const { targets } = useEmissionTargets({ status: 'active' });
  const { initiatives } = useReductionInitiatives();
  const actionLevers = initiatives.map((initiative) => ({
    id: initiative.id,
    name: initiative.name,
    estimatedAnnualReduction: initiative.estimatedAnnualReduction,
  }));

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');

  const handleBack = () => {
    void navigate({ to: '/ghg/scenarios' });
  };

  const handleEdit = (data: EmissionScenarioFormValues) => {
    updateScenario.mutate(
      {
        scenarioId,
        data: {
          name: data.name,
          description: data.description || undefined,
          initiatives: data.initiatives,
          customAdjustments: data.customAdjustments,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('toast.scenarioUpdatedSuccessfully'));
          setIsEditOpen(false);
        },
        onError: () => {
          toast.error(t('toast.failedToUpdateScenario'));
        },
      }
    );
  };

  const handleYearByYearSave = async (customAdjustments: CustomAdjustmentInput[]) => {
    try {
      await updateScenario.mutateAsync({
        scenarioId,
        data: {
          customAdjustments,
        },
      });
      toast.success(t('scenarioDetail.yearByYearUpdated'));
    } catch {
      toast.error(t('scenarioDetail.yearByYearUpdateFailed'));
      throw new Error('Failed to update year-by-year projection');
    }
  };

  const handleStatusChange = (status: ScenarioStatus) => {
    updateScenario.mutate(
      {
        scenarioId,
        data: { status },
      },
      {
        onSuccess: () => {
          toast.success(
            t('scenarioDetail.statusChangedTo', { status: status === 'active' ? t('scenarioDetail.statusActive') : t('scenarioDetail.statusNotActive') })
          );
        },
        onError: () => {
          toast.error(t('scenarioDetail.statusUpdateFailed'));
        },
      }
    );
  };

  const handleDelete = () => {
    deleteScenario.mutate(scenarioId, {
      onSuccess: () => {
        toast.success(t('toast.scenarioDeletedSuccessfully'));
        void navigate({ to: '/ghg/scenarios' });
      },
      onError: () => {
        toast.error(t('toast.failedToDeleteScenario'));
      },
    });
  };

  const handleDuplicate = () => {
    if (!duplicateName.trim()) {
      toast.error(t('toast.pleaseEnterANameForTheDuplicateScenario'));
      return;
    }

    duplicateScenario.mutate(
      {
        scenarioId,
        data: { name: duplicateName },
      },
      {
        onSuccess: (newScenario) => {
          toast.success(t('toast.scenarioDuplicatedSuccessfully'));
          setIsDuplicateOpen(false);
          setDuplicateName('');
          void navigate({
            to: '/ghg/scenarios/$scenarioId',
            params: { scenarioId: newScenario.id },
          });
        },
        onError: () => {
          toast.error(t('toast.failedToDuplicateScenario'));
        },
      }
    );
  };

  const openDuplicateDialog = () => {
    setDuplicateName(scenario ? `${scenario.name} (Copy)` : '');
    setIsDuplicateOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4 md:grid-cols-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (isError || !scenario) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('scenarioDetail.back')}
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('scenarioDetail.scenarioNotFound')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl">
        <ScenarioDetail
          scenario={scenario}
          onEdit={() => {
            setIsEditOpen(true);
          }}
          onDuplicate={openDuplicateDialog}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          isUpdatingStatus={updateScenario.isPending}
          isDeleting={deleteScenario.isPending}
          onYearByYearSave={handleYearByYearSave}
          isSavingYearByYear={updateScenario.isPending}
        />
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('scenarioDetail.editScenarioTitle')}</DialogTitle>
            <DialogDescription>
              {t('scenarioDetail.editScenarioDescription')}
            </DialogDescription>
          </DialogHeader>
          <ScenarioForm
            scenario={scenario}
            targets={targets}
            initiatives={actionLevers}
            onSubmit={handleEdit}
            onCancel={() => {
              setIsEditOpen(false);
            }}
            isSubmitting={updateScenario.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <AlertDialog open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('scenarioDetail.duplicateScenarioTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('scenarioDetail.duplicateScenarioDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="duplicate-name">{t('scenarioDetail.newScenarioName')}</Label>
            <Input
              id="duplicate-name"
              value={duplicateName}
              onChange={(e) => {
                setDuplicateName(e.target.value);
              }}
              placeholder={t('scenarioDetail.enterDuplicateName')}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDuplicateName('');
              }}
            >
              {t('scenarioDetail.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDuplicate}
              disabled={duplicateScenario.isPending || !duplicateName.trim()}
            >
              {duplicateScenario.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('scenarioDetail.duplicate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
